import {Injectable, NgZone} from '@angular/core';
import {Observable} from 'rxjs';

import {ExgMapMarkerDirective} from '../../exg-map-marker/exg-map-marker.directive';

import {ExgMapGoogleAPIWrapper} from '../exg-map-google-api-wrapper.service';

@Injectable()
export class ExgMapMarkerManagerService {
  protected _markers: Map<ExgMapMarkerDirective, Promise<google.maps.Marker>> =
    new Map<ExgMapMarkerDirective, Promise<google.maps.Marker>>();

  constructor(protected _mapsWrapper: ExgMapGoogleAPIWrapper, protected _zone: NgZone) {
  }

  async convertAnimation(uiAnim: keyof typeof google.maps.Animation | null): Promise<google.maps.Animation | null> {
    if (uiAnim === null) {
      return null;
    } else {
      return this._mapsWrapper.getNativeMap().then(() => google.maps.Animation[uiAnim]);
    }
  }

  deleteMarker(markerDirective: ExgMapMarkerDirective): Promise<void> {
    const markerPromise = this._markers.get(markerDirective);
    if (markerPromise == null) {
      // marker already deleted
      return Promise.resolve();
    }
    return markerPromise.then((marker: google.maps.Marker) => {
      return this._zone.run(() => {
        marker.setMap(null);
        this._markers.delete(markerDirective);
      });
    });
  }

  updateMarkerPosition(marker: ExgMapMarkerDirective): Promise<void> {
    // @ts-ignore
    return this._markers.get(marker).then(
      (m: google.maps.Marker) => m.setPosition({lat: marker.latitude as number, lng: marker.longitude as number}));
  }

  updateTitle(marker: ExgMapMarkerDirective): Promise<void> {
    // @ts-ignore
    return this._markers.get(marker).then((m: google.maps.Marker) => m.setTitle(marker.title));
  }

  updateLabel(marker: ExgMapMarkerDirective): Promise<void> {
    // @ts-ignore
    return this._markers.get(marker).then((m: google.maps.Marker) => {
      m.setLabel(marker.label as any);
    });
  }

  updateDraggable(marker: ExgMapMarkerDirective): Promise<void> {
    // @ts-ignore
    return this._markers.get(marker).then((m: google.maps.Marker) => m.setDraggable(marker.draggable));
  }

  updateIcon(marker: ExgMapMarkerDirective): Promise<void> {
    // @ts-ignore
    return this._markers.get(marker).then((m: google.maps.Marker) => m.setIcon(marker.iconUrl));
  }

  updateOpacity(marker: ExgMapMarkerDirective): Promise<void> {
    // @ts-ignore
    return this._markers.get(marker).then((m: google.maps.Marker) => m.setOpacity(marker.opacity));
  }

  updateVisible(marker: ExgMapMarkerDirective): Promise<void> {
    // @ts-ignore
    return this._markers.get(marker).then((m: google.maps.Marker) => m.setVisible(marker.visible));
  }

  updateZIndex(marker: ExgMapMarkerDirective): Promise<void> {
    // @ts-ignore
    return this._markers.get(marker).then((m: google.maps.Marker) => m.setZIndex(marker.zIndex));
  }

  updateClickable(marker: ExgMapMarkerDirective): Promise<void> {
    // @ts-ignore
    return this._markers.get(marker).then((m: google.maps.Marker) => m.setClickable(marker.clickable));
  }

  async updateAnimation(marker: ExgMapMarkerDirective): Promise<void> {
    const m = await this._markers.get(marker);
    if (m) {
      m.setAnimation(await this.convertAnimation(marker.animation as any));
    }
  }

  addMarker(marker: ExgMapMarkerDirective): void {
    const markerPromise = new Promise<google.maps.Marker>(async (resolve) =>
      this._mapsWrapper.createMarker({
        position: {lat: marker.latitude as number, lng: marker.longitude as number},
        label: marker.label,
        draggable: marker.draggable,
        icon: marker.iconUrl,
        opacity: marker.opacity,
        visible: marker.visible,
        zIndex: marker.zIndex,
        title: marker.title,
        clickable: marker.clickable,
        animation: await this.convertAnimation(marker.animation as any) || undefined,
      }).then(resolve));
    this._markers.set(marker, markerPromise);
  }

  getNativeMarker(marker: ExgMapMarkerDirective): Promise<google.maps.Marker> | undefined {
    return this._markers.get(marker);
  }

  createEventObservable<T extends (google.maps.MapMouseEvent | void)>(
    eventName: any,
    marker: ExgMapMarkerDirective): Observable<T> {
    return new Observable(observer => {
      this._markers.get(marker)?.then(m =>
        m.addListener(eventName, (e: any) => this._zone.run(() => observer.next(e)))
      );
    });
  }
}
